import { useCallback, useEffect, useMemo, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';

import './profile-confirm.scss';

type Status =
	| 'loading'
	| 'ready'
	| 'done-half'
	| 'applied'
	| 'cancelled'
	| 'expired'
	| 'invalid';

interface View {
	success: boolean;
	status: Status;
	type?: 'email' | 'phone';
	side?: 'current' | 'next';
	current?: string;
	next?: string;
	waitingOn?: 'current' | 'next';
	message: string;
}

/**
 * WHERE AN APPROVAL LINK LANDS.
 *
 * Public — no session. The link is opened from an inbox, routinely in a
 * browser that has never signed in, and requiring a login to approve a change
 * would defeat the tripwire for exactly the person who most needs it: the one
 * whose account has just been redirected by somebody else.
 *
 * THE PAGE EXISTS TO ABSORB PREFETCHES. Corporate mail scanners and
 * link-preview crawlers fetch every URL in a message. If the emailed link hit
 * the API directly, Outlook would approve the change before the recipient ever
 * saw the mail. So the load is a read-only GET that renders what is about to
 * happen, and the mutation is a PATCH fired only by a real click on the button
 * below. A scanner loads this page and stops.
 */
export const ProfileConfirm = () => {
	const { get, patch } = useNetwork();
	const [view, setView] = useState<View>({
		success: false,
		status: 'loading',
		message: '',
	});
	const [busy, setBusy] = useState(false);

	const params = useMemo(
		() => new URLSearchParams(window.location.search),
		[]
	);
	/* `approval`, not `code` — see the note on `linkFor` in clients.api. `code`
	   is still accepted so links already sitting in an inbox keep working, but
	   nothing new is minted with it. */
	const code = params.get('approval') ?? params.get('code') ?? '';
	/* The cancel link is the SAME code with a different action — both actions
	   are offered in one envelope to one inbox, so they need no second secret. */
	const isCancel = params.get('action') === 'cancel';

	useEffect(() => {
		if (!code) {
			setView({ success: false, status: 'invalid', message: 'No code in this link.' });
			return;
		}
		get(`${API_URL.PROFILE_CHANGE_APPROVALS}/${code}`).then((resp: any) => {
			const d = resp?.data ?? resp;
			if (!d) {
				setView({
					success: false,
					status: 'invalid',
					message: 'We could not read this link.',
				});
				return;
			}
			setView(d as View);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [code]);

	const commit = useCallback(
		async (action: 'approve' | 'confirm' | 'cancel') => {
			setBusy(true);
			try {
				const resp: any = await patch(
					`${API_URL.PROFILE_CHANGE_APPROVALS}/${code}`,
					{ action }
				);
				const d = resp?.data ?? resp;
				if (d?.status) {
					setView(d as View);
					/* Tell any other tab of this browser that the account may have
					   moved, so the sidebar updates without a reload. Same-origin
					   only and best-effort — the panel also reconciles on focus
					   and on its poll, so a browser without BroadcastChannel just
					   resolves a moment later. */
					try {
						const ch = new BroadcastChannel('simplici-profile-change');
						ch.postMessage({status: d.status});
						ch.close();
					} catch {
						/* no BroadcastChannel — focus/poll covers it */
					}
				}
				else
					setView(v => ({
						...v,
						success: false,
						status: 'invalid',
						message: d?.message ?? 'That did not work. Ask for a new link.',
					}));
			} finally {
				setBusy(false);
			}
		},
		[code, patch]
	);

	const { status } = view;
	const noun = view.type === 'phone' ? 'phone number' : 'email address';

	/* An invalid or expired code renders NO values — printing the addresses of
	   a dead request would leak them to anyone guessing at codes. */
	const showValues =
		status === 'ready' || status === 'done-half' || status === 'applied';

	return (
		<div className="sv2-scope sv2 pconfirm">
			<div className="pconfirm__card">
				<div className="pconfirm__rule" />

				{status === 'loading' && (
					<h1 className="pconfirm__h">Checking this link…</h1>
				)}

				{status === 'ready' && (
					<>
						<h1 className="pconfirm__h">
							{isCancel
								? `Cancel this ${noun} change?`
								: view.side === 'next'
								? 'Confirm this is your email address'
								: `Approve the change to your ${noun}`}
						</h1>
						<p className="pconfirm__p">
							{isCancel
								? 'Nothing has been changed yet. Cancelling stops this request and every link in it.'
								: view.side === 'next'
								? 'Confirming proves this inbox can receive mail, so a typo can’t lock you out.'
								: `Nothing changes until you approve it here.`}
						</p>
					</>
				)}

				{status === 'done-half' && (
					<>
						<h1 className="pconfirm__h">One down — one to go</h1>
						<p className="pconfirm__p">
							{view.waitingOn === 'next'
								? 'Now click the link we sent to the new address to finish.'
								: 'Now click the link we sent to your current address to finish.'}
						</p>
						<p className="pconfirm__p">
							Your Simplici panel already shows this one as approved.
						</p>
					</>
				)}

				{status === 'applied' && (
					<>
						<h1 className="pconfirm__h">
							{view.type === 'phone' ? 'Phone number updated' : 'Email address updated'}
						</h1>
						<p className="pconfirm__p">{view.message}</p>
						{/* Says out loud what the panel is about to do on its own. The
						    residual wait is a few seconds; telling the reader to expect
						    it is what stops those seconds reading as "nothing
						    happened" and sending them off to hunt for a refresh. */}
						<p className="pconfirm__p">
							You can close this — your Simplici panel updates itself in a few
							seconds.
						</p>
					</>
				)}

				{status === 'cancelled' && (
					<>
						<h1 className="pconfirm__h">Cancelled</h1>
						<p className="pconfirm__p">{view.message}</p>
					</>
				)}

				{status === 'expired' && (
					<>
						<h1 className="pconfirm__h">This link has expired</h1>
						<p className="pconfirm__p">
							Approval links are good for two hours. Open your profile and request
							the change again — you’ll get a fresh email straight away.
						</p>
					</>
				)}

				{status === 'invalid' && (
					<>
						<h1 className="pconfirm__h">This link isn’t valid</h1>
						<p className="pconfirm__p">{view.message}</p>
					</>
				)}

				{showValues && view.current && (
					<div className="pconfirm__panel">
						<div className="pconfirm__row">
							<span className="pconfirm__k">
								{status === 'applied' ? 'Previous' : 'Current'}
							</span>
							<span className="pconfirm__v">{view.current}</span>
						</div>
						<div className="pconfirm__row">
							<span className="pconfirm__k">
								{status === 'applied' ? 'Now' : 'New'}
							</span>
							<span className="pconfirm__v pconfirm__v--strong">{view.next}</span>
						</div>
					</div>
				)}

				{status === 'ready' && (
					<div className="pconfirm__acts">
						{isCancel ? (
							<button
								type="button"
								className="pconfirm__btn pconfirm__btn--danger"
								disabled={busy}
								onClick={() => commit('cancel')}
							>
								{busy ? 'Cancelling…' : 'Cancel this request'}
							</button>
						) : (
							<>
								<button
									type="button"
									className="pconfirm__btn"
									disabled={busy}
									onClick={() => commit(view.side === 'next' ? 'confirm' : 'approve')}
								>
									{busy
										? 'Working…'
										: view.side === 'next'
										? 'Confirm this address'
										: 'Approve this change'}
								</button>
								<button
									type="button"
									className="pconfirm__ghost"
									disabled={busy}
									onClick={() => commit('cancel')}
								>
									I didn’t request this
								</button>
							</>
						)}
					</div>
				)}

				{(status === 'applied' || status === 'cancelled' || status === 'expired') && (
					<div className="pconfirm__acts">
						<a className="pconfirm__btn" href="/">
							Go to Simplici
						</a>
					</div>
				)}
			</div>
		</div>
	);
};
